// AlphaPhaseInfernoBanner.tsx
import React from "react";
import { styled, keyframes } from "@mui/material";

export interface AlphaPhaseInfernoBannerProps {
    className?: string;
    style?: React.CSSProperties;
    title?: string;
    subtitle?: string;
    /** 0.6..1.6 — множитель интенсивности анимаций и свечения */
    intensity?: number;
    /** Куда отправлять пользователя по клику */
    href?: string;
    /** Открывать ссылку в новой вкладке */
    newTab?: boolean;
    /** Текст подсказки/CTA под заголовком */
    hint?: string;
}

/* ===== Animations ===== */
const breathe = keyframes`
  0%   { transform: translateY(2%) scaleY(0.98); opacity: .95; }
  50%  { transform: translateY(-1%) scaleY(1.02); opacity: 1; }
  100% { transform: translateY(2%) scaleY(0.98); opacity: .95; }
`;
const flameWave = keyframes`
  0%   { transform: translateY(8%) scaleY(0.96) skewY(-1deg); }
  50%  { transform: translateY(-6%) scaleY(1.06) skewY(1deg); }
  100% { transform: translateY(8%) scaleY(0.96) skewY(-1deg); }
`;
const shimmer = keyframes`
  0%   { background-position: 0% 60%, 50% 100%, 50% 100%; }
  50%  { background-position: 100% 40%, 48% 96%, 52% 96%; }
  100% { background-position: 0% 60%, 50% 100%, 50% 100%; }
`;
const heatHaze = keyframes`
  0%   { transform: translateY(0) skewY(0deg); opacity:.25 }
  50%  { transform: translateY(-2px) skewY(.6deg); opacity:.38 }
  100% { transform: translateY(0) skewY(0deg); opacity:.25 }
`;
const sparkRise = keyframes`
  0%   { transform: translateY(0) translateX(0) scale(.8); opacity: .0; }
  10%  { opacity: .9; }
  100% { transform: translateY(-180%) translateX(12px) scale(1.05); opacity: 0; }
`;

/* ===== Styled ===== */
const Root = styled("section")({
    minHeight: "100dvh",
    width: "100%",
    display: "grid",
    placeItems: "center",
    padding: 16,
    boxSizing: "border-box",
});

const LinkWrap = styled("a")({
    textDecoration: "none",
    outline: "none",
    WebkitTapHighlightColor: "transparent",
    "&:focus-visible .inferno": {
        boxShadow:
            "0 0 0 3px rgba(255,255,255,0.35), 0 12px 38px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.06)",
    },
});

const Inferno = styled("div")<{ $intensity: number }>(({ $intensity }) => {
    const i = Math.max(0.6, Math.min(1.6, $intensity || 1));
    return {
        "--fire-amber": "255, 191, 64",
        "--fire-gold": "255, 220, 120",
        "--fire-orange": "255, 146, 43",
        "--fire-red": "255, 68, 23",
        "--fire-crimson": "230, 32, 32",
        
        position: "relative",
        width: "fit-content",
        maxWidth: "92vw",
        padding: 20,
        borderRadius: 16,
        border: "1px solid rgba(255,255,255,0.18)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        boxShadow:
            "0 14px 40px rgba(0,0,0,0.48), inset 0 0 0 1px rgba(255,255,255,0.06)",
        overflow: "hidden",
        cursor: "pointer",
        
        // стекло + тёплый градиент
        background:
            "linear-gradient(180deg, rgba(255,255,255,0.10), rgba(20,20,24,0.55))",
        
        // «дыхание»
        animation: `${breathe} ${3.8 / i}s ease-in-out infinite`,
        
        // Hover/active акценты
        transition: "transform .18s ease, box-shadow .18s ease",
        "&:hover": {
            transform: "translateY(-1px)",
            boxShadow:
                "0 18px 46px rgba(0,0,0,0.52), inset 0 0 0 1px rgba(255,255,255,0.08)",
        },
        "&:active": {
            transform: "translateY(0)",
            boxShadow:
                "0 10px 30px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.08)",
        },
        
        "@media (max-width: 480px)": {
            padding: 14
        },
        
        // Огненная аура
        "::before": {
            content: "\"\"",
            position: "absolute",
            inset: "-20% -16% -24% -16%",
            borderRadius: 28,
            zIndex: 0,
            filter: `blur(${26 * i}px) saturate(${1 + 0.25 * i})`,
            background:
                `radial-gradient(60% 70% at 50% 100%,
           rgba(var(--fire-gold),${0.55 * i}) 0%,
           rgba(var(--fire-orange),${0.45 * i}) 35%,
           rgba(var(--fire-red),${0.35 * i}) 60%,
           rgba(0,0,0,0) 75%)`,
            animation: `${flameWave} ${4.6 / i}s ease-in-out infinite`,
        },
        
        // Языки пламени
        "::after": {
            content: "\"\"",
            position: "absolute",
            inset: "-10% -8% -14% -8%",
            zIndex: 0,
            mixBlendMode: "screen",
            opacity: 0.9,
            filter: `blur(${14 * i}px) saturate(${1.15 + 0.2 * i})`,
            background:
                `conic-gradient(from 180deg at 50% 95%,
            rgba(var(--fire-amber),0.22),
            rgba(var(--fire-orange),0.36),
            rgba(var(--fire-red),0.32),
            rgba(var(--fire-amber),0.22))
         , repeating-conic-gradient(from 180deg at 50% 100%,
            rgba(var(--fire-gold),0.18) 0deg,
            rgba(var(--fire-gold),0.18) 6deg,
            transparent 8deg,
            transparent 12deg)`,
            backgroundBlendMode: "screen",
            animation: `${flameWave} ${3.2 / i}s ease-in-out infinite`,
            animationDelay: "0.25s",
        },
        
        // Haze
        "& .haze": {
            position: "absolute",
            inset: "8% 6% 10% 6%",
            zIndex: 3,
            pointerEvents: "none",
            background:
                `linear-gradient(180deg,
           rgba(255,255,255,0.03),
           rgba(255,255,255,0.0)),
         radial-gradient(140% 60% at 50% 120%,
           rgba(var(--fire-gold),0.10),
           rgba(0,0,0,0) 60%)`,
            mixBlendMode: "screen",
            animation: `${heatHaze} ${2.8 / i}s ease-in-out infinite`,
        },
        
        // Glow
        "& .glow": {
            position: "absolute",
            inset: 0,
            zIndex: 2,
            pointerEvents: "none",
            background:
                `radial-gradient(120% 120% at 50% 115%,
           rgba(var(--fire-gold),${0.22 * i}) 0%,
           rgba(var(--fire-orange),${0.12 * i}) 32%,
           transparent 58%)
         , radial-gradient(80% 60% at 50% -10%,
           rgba(255,255,255,0.05),
           transparent 60%)`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "160% 160%, 100% 100%",
            animation: `${shimmer} ${8.5 / i}s ease-in-out infinite`,
        },
        
        // Искры
        "& .sparks": {
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 8,
            height: 100,
            pointerEvents: "none",
            zIndex: 4,
            overflow: "hidden",
        },
        "& .spark": {
            position: "absolute",
            bottom: 0,
            width: 3,
            height: 3,
            borderRadius: 3,
            background:
                `radial-gradient(circle at 50% 50%,
           rgba(255,255,255,1) 0%,
           rgba(var(--fire-gold),0.95) 55%,
           rgba(var(--fire-orange),0.0) 100%)`,
            filter: "drop-shadow(0 0 8px rgba(255,190,80,0.95))",
            animation: `${sparkRise} ${2.2 / i}s linear infinite`,
        },
        
        // «кликни» бэйдж
        "& .hint": {
            position: "relative",
            zIndex: 5,
            marginTop: 8,
            textAlign: "center",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            color: "rgb(255, 244, 224)",
            textShadow:
                "0 0 10px rgba(255,190,80,0.9), 0 0 24px rgba(255,120,40,0.55)",
            borderTop: "1px dashed rgba(255,255,255,0.18)",
            paddingTop: 8,
            userSelect: "none",
        },
        
        // reduced motion
        "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
            "::before": {
                animation: "none"
            },
            "::after": {
                animation: "none"
            },
            "& .haze": {
                animation: "none"
            },
            "& .spark": {
                animation: "none"
            },
            "& .glow": {
                animation: "none"
            },
        },
    };
});

const Title = styled("div")({
    position: "relative",
    zIndex: 5,
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: 0.4,
    color: "#EAF1F9",
    textAlign: "center",
    marginBottom: 10,
    textTransform: "uppercase",
    opacity: 0.95,
    "@media (max-width: 480px)": {
        fontSize: 10, marginBottom: 8
    },
});

const BigLine = styled("div")({
    position: "relative",
    zIndex: 5,
    fontWeight: 900,
    fontSize: 28,
    lineHeight: 1.1,
    letterSpacing: 0.5,
    textAlign: "center",
    color: "rgb(255, 244, 224)",
    textShadow:
        "0 0 14px rgba(255,190,80,0.85), 0 0 36px rgba(255,120,40,0.55), 0 2px 0 rgba(0,0,0,0.25)",
    "@media (max-width: 480px)": {
        fontSize: 18
    },
});

const Footer = styled("p")({
    position: "relative",
    zIndex: 5,
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: 0.4,
    color: "rgba(233,238,245,0.82)",
    textAlign: "center",
    marginBottom: 0,
    marginTop: 10,
    opacity: 0.95,
    "@media (max-width: 480px)": {
        fontSize: 10, marginTop: 6
    },
});

/* ===== Component ===== */
export default function AlphaPhaseInfernoBanner({
    className,
    style,
    title = "Alpha testing — 2 — invite-only",
    subtitle = "Upgraded forecasting engine, Empowered AI, More Stocks!",
    intensity = 1,
    href = "https://insight-questor-mvp.web.app/",
    newTab = true,
    hint = "Click to explore",
}: AlphaPhaseInfernoBannerProps) {
    const target = newTab ? "_blank" : "_self";
    const rel = newTab ? "noopener noreferrer" : undefined;
    
    return (
        <Root className={className} style={style}>
            <LinkWrap
                href={href}
                target={target}
                rel={rel}
                aria-label="Alpha testing is in full swing! Click to explore"
                title="Click to explore"
            >
                <Inferno className="inferno" $intensity={intensity} role="link">
                    <Title>{title}</Title>
                    <BigLine>Alpha testing is in full swing!</BigLine>
                    
                    {/* искры */}
                    <div className="sparks" aria-hidden>
                        {Array.from({
                            length: 22
                        }).map((_, i) => {
                            const left = 4 + (i * 100) / 22;
                            const delay = (i % 7) * 0.18;
                            const duration = 1.7 + (i % 5) * 0.22;
                            const size = 2 + (i % 3);
                            const xJitter = (i % 2 === 0 ? -1 : 1) * (2 + (i % 4));
                            return (
                                <span
                                    key={i}
                                    className="spark"
                                    style={{
                                        left: `${left}%`,
                                        width: size,
                                        height: size,
                                        animationDelay: `${delay}s`,
                                        animationDuration: `${duration}s`,
                                        transform: `translateX(${xJitter}px)`,
                                    }}
                                />
                            );
                        })}
                    </div>
                    
                    <div className="glow" aria-hidden />
                    <div className="haze" aria-hidden />
                    
                    <Footer>{subtitle}</Footer>
                    <div className="hint">{hint}</div>
                </Inferno>
            </LinkWrap>
        </Root>
    );
}
